/* eslint complexity: ["error", {max: 9}] -- applies to entire file */
import { useEffect, useMemo, useState } from "react";

import isNil from "lodash/isNil";

import { getUserOverviewCardProps } from "@/app/(app)/(onboarding)/verify-information";
import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { OnboardingScreenPath } from "@/constants/Navigation";
import { ConsentText } from "@/constants/Onboarding";
import { OnboardingConsentTestID, TextTestID } from "@/constants/TestID";
import { useOnboarding } from "@/context-providers/OnboardingProvider";
import { router, useLocalSearchParams } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/services/Analytics";
import { useGetUsStatesQuery } from "@/store/api/userEnums";
import { useAppUserSelector } from "@/store/reducers/appUser";
import ConsentSection from "@/ui/components/onboarding/consent/ConsentSection";
import { getUserOverviewCard } from "@/ui/components/onboarding/OnboardingVerifyInfoPage";
import { Button, FAIconName, IconButton, Text } from "@/ui/core";
import { HStack, ScreenContainer, VStack } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = OnboardingConsentTestID.Primary;

/* eslint max-statements: ["error", {max: 30}] -- This file is complex */
/* eslint max-lines-per-function: ["error", {max: 180}] -- we need to handle for both patient and proxy users that require consent  */
const Screen = () => {
  const { track, trackPageView } = useAnalytics();
  const { isAddToCalendarEnabled } = useFeatureFlags();

  const {
    consents: { isTermsAndPrivacyAccepted, isTermsAndPrivacyAcceptedManually },
    isRecordingConsentAlreadyExist,
    onPatientConsentChange,
    onTermsAndPrivacyChange: handleTermsAndPrivacyChange,
    patientConsentStates,
    patientsNeedingConsent,
    shouldShowConsentToRecording,
  } = useOnboarding();
  const { shouldShowPatientCards } = useLocalSearchParams<{
    shouldShowPatientCards?: string;
  }>();
  const isAdditionalConsent = shouldShowPatientCards === "true";
  const { externalId: appUserExternalId, isPatient: isAppUserPatient } =
    useAppUserSelector();

  // maintaining a local state for the loading state of the consent action
  // since we have multiple consent api calls that can be triggered and
  // we want to show the loading state for the entire consent action
  const [isConsentLoading, setIsConsentLoading] = useState<boolean>(false);
  const { data: usStates } = useGetUsStatesQuery();

  const patientsNeedingConsentLocal = useMemo(() => {
    return patientsNeedingConsent ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patientsInformation = patientsNeedingConsentLocal.map((profile) =>
    getUserOverviewCardProps({ profile, usStates }),
  );
  useEffect(() => {
    trackPageView(OnboardingAnalyticsPageView.Consent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleNext = async () => {
    track(OnboardingAnalyticsEvent.NextButtonPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Consent,
    });
    // only default to false consent if the user is SHOWN the consent card
    // if user is NOT shown the consent card (ie due to being a minor, or a support for an adult patient)
    // then we do not save anything in the backend as that would be misleading and inaccurate

    setIsConsentLoading(true);
    // user is proxy
    const isProxyUserShownConsent = !isAppUserPatient;

    // user did not take any action on the consent to record card
    // user is patient
    // so we default to false consent
    const isPatientUserShownConsent =
      isNil(patientConsentStates[appUserExternalId]) && isAppUserPatient;

    // consent to record is shown
    if (shouldShowConsentToRecording) {
      if (isPatientUserShownConsent) {
        await onPatientConsentChange({
          isChecked: false,
          patientId: appUserExternalId,
        });
      } else if (isProxyUserShownConsent) {
        await Promise.all(
          patientsNeedingConsentLocal.map(async (patient) => {
            // if the patient is not in the patientConsentStates, we default to false consent
            if (!isNil(patientConsentStates[patient.externalId])) {
              return;
            }
            await onPatientConsentChange({
              isChecked: false,
              patientId: patient.externalId,
            });
          }),
        );
      }
    }

    setIsConsentLoading(false);
    //  we are replacing the route to the welcome screen if this is not an additional consent
    // otherwise we are dismissing the modal
    // NOTE: adding requestAnimationFrame prevents a runtime exception on Android
    // where SVG views crash during screen transitions in Fabric/New Architecture
    if (isAdditionalConsent) {
      requestAnimationFrame(() => {
        router.dismiss();
      });
    } else {
      requestAnimationFrame(() => {
        router.replace(OnboardingScreenPath.Welcome);
      });
    }
  };

  const isNextEnabled = isTermsAndPrivacyAccepted;
  const userOverviewCards = patientsInformation.map((patientInfo) =>
    getUserOverviewCard({ ...patientInfo, isAdditionalConsent }),
  );

  // Determine back button route based on feature flag
  const backRoute = isAddToCalendarEnabled
    ? OnboardingScreenPath.CalendarSync
    : OnboardingScreenPath.Notifications;

  return (
    <ScreenContainer
      backgroundColor={ColorToken.Primary10}
      isScrollView
      isStackLayout
    >
      <VStack
        flex={1}
        gap={24}
        justifyContent="space-between"
        paddingBottom={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingHorizontal={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingTop={LayoutToken.ModalHeaderPadding}
        testID={baseTestID}
      >
        <VStack gap={24}>
          <VStack gap={12}>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, TextTestID.Title)}
              variant="h2"
            >
              {isAdditionalConsent
                ? ConsentText.AdditionalConsentTitle
                : ConsentText.ConsentSectionTitle}
            </Text>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, TextTestID.Description)}
              variant="body"
            >
              {isAdditionalConsent
                ? ConsentText.AdditionalConsentSectionDescription
                : ConsentText.ConsentSectionDescription}
            </Text>
          </VStack>
          {isAdditionalConsent && userOverviewCards}
          <ConsentSection
            isRecordingConsentAlreadyExist={isRecordingConsentAlreadyExist}
            isTermsAndPrivacyAccepted={isTermsAndPrivacyAccepted}
            isTermsAndPrivacyAcceptedManually={
              isTermsAndPrivacyAcceptedManually
            }
            onPatientConsentChange={onPatientConsentChange}
            onTermsAndPrivacyChange={handleTermsAndPrivacyChange}
            patientConsentStates={patientConsentStates}
            patientsNeedingConsent={patientsNeedingConsentLocal}
            shouldShowConsentToRecording={shouldShowConsentToRecording}
          />
        </VStack>
        <HStack gap={24} justifyContent="flex-end">
          {!isAdditionalConsent && (
            <IconButton
              iconName={FAIconName.ArrowLeft}
              onPress={() => router.replace(backRoute)}
              shouldShowBorder
              testID={joinTestIDs(baseTestID, "back")}
            />
          )}
          <Button
            isDisabled={!isNextEnabled || isConsentLoading}
            isFullWidth
            isLoading={isConsentLoading}
            leftIcon={isAdditionalConsent ? undefined : FAIconName.ArrowRight}
            onPress={handleNext}
            testID={joinTestIDs(baseTestID, "Next")}
            title={isAdditionalConsent ? "Save changes" : "Next"}
            variant="primary.on_background"
          />
        </HStack>
      </VStack>
    </ScreenContainer>
  );
};

export default Screen;
